"use client";

import { useEffect, useRef, useState } from "react";
import { rightArrowIcon } from "../../assets";
import ModalContainer from "../modal/modal-container";
import styles from "./index.module.css";
import Skeleton from "react-loading-skeleton";

const MarketplaceCategories = ({
  closeModal,
  modalBackButton,
  categories = [],
  setSearchTerm,
  initialActiveCategoryId = null,
}) => {
  const [activeCategoryId, setActiveCategoryId] = useState(
    initialActiveCategoryId || categories?.[0]?.categoryId || null,
  );

  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [visibleLimit, setVisibleLimit] = useState(14);

  const subContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  /* ---------------------------------------------
     Responsive limit logic
  ----------------------------------------------*/
  useEffect(() => {
    const updateLimit = () => {
      if (window.innerWidth <= 840) {
        setVisibleLimit(11); // 👈 load less than 12
      } else {
        setVisibleLimit(14);
      }
    };

    updateLimit();
    window.addEventListener("resize", updateLimit);

    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  /* ---------------------------------------------
     Scroll active category into view
  ----------------------------------------------*/
  useEffect(() => {
    if (!subContainerRef.current || !activeCategoryId) return;

    const activeEl = subContainerRef.current.querySelector(
      `[data-category="${activeCategoryId}"]`,
    );

    activeEl?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [activeCategoryId]);

  /* ---------------------------------------------
     Detect when scrolling STOPS
  ----------------------------------------------*/
  useEffect(() => {
    const container = subContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const groups = container.querySelectorAll("[data-category]");
        const containerRect = container.getBoundingClientRect();

        let maxVisible = 0;
        let mostVisibleId = activeCategoryId;

        groups.forEach((group) => {
          const rect = group.getBoundingClientRect();

          const visibleHeight =
            Math.min(rect.bottom, containerRect.bottom) -
            Math.max(rect.top, containerRect.top);

          const ratio = visibleHeight / rect.height;

          if (ratio > maxVisible) {
            maxVisible = ratio;
            mostVisibleId = group.getAttribute("data-category");
          }
        });

        if (mostVisibleId && mostVisibleId !== activeCategoryId) {
          setActiveCategoryId(mostVisibleId);
        }

        if (mostVisibleId !== expandedCategoryId) {
          setExpandedCategoryId(null);
        }
      }, 150);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [activeCategoryId, expandedCategoryId]);

  return (
    <ModalContainer closeModal={closeModal} modalBackButton={modalBackButton}>
      <div className={styles.modalContainer}>
        {/* LEFT SIDE */}
        <div className={styles.modalCategories}>
          {categories.map((mainCat) => (
            <div
              key={mainCat.categoryId}
              className={`${styles.modalCategoriesItem} ${
                activeCategoryId === mainCat.categoryId
                  ? styles.activeCategory
                  : ""
              }`}
              onClick={() => setActiveCategoryId(mainCat.categoryId)}
            >
              <span>{mainCat.name}</span>
              <span>{rightArrowIcon}</span>
            </div>
          ))}
        </div>

        {/* RIGHT SIDE */}
        <div className={styles.modalSubCategories} ref={subContainerRef}>
          {categories.map((mainCat) => {
            const subList = mainCat.SubCategory || [];
            const isExpanded = expandedCategoryId === mainCat.categoryId;

            const shouldShowViewMore = subList.length > visibleLimit;

            const visibleSubs =
              shouldShowViewMore && !isExpanded
                ? subList.slice(0, visibleLimit)
                : subList;

            return (
              <div
                key={mainCat.categoryId}
                data-category={mainCat.categoryId}
                className={styles.subCategoryGroup}
              >
                <h3>{mainCat.name}</h3>

                <div
                  className={styles.subCategoryContainer}
                  style={
                    isExpanded
                      ? {
                          maxHeight: "100%", // control expanded scroll area height
                          overflowY: "auto",
                          scrollbarWidth: "none",
                        }
                      : undefined
                  }
                >
                  {visibleSubs.map((sub) => (
                    <div
                      key={sub._id}
                      className={styles.subCategoryItem}
                      onClick={() => {
                        setSearchTerm(sub.name);
                        closeModal();
                      }}
                    >
                      <div className={styles.subImageWrapper}>
                        {!loadedImages[sub._id] && (
                          <Skeleton circle width={60} height={60} />
                        )}

                        {sub.media?.[0] && (
                          <img
                            src={sub.media[0].mediaUrl}
                            alt={sub.name}
                            className={styles.subImage}
                            style={{
                              opacity: loadedImages[sub._id] ? 1 : 0,
                            }}
                            onLoad={() =>
                              setLoadedImages((prev) => ({
                                ...prev,
                                [sub._id]: true,
                              }))
                            }
                          />
                        )}
                      </div>

                      <span className={styles.subName}>{sub.name}</span>
                    </div>
                  ))}
                  {/* View More */}
                  {shouldShowViewMore && !isExpanded && (
                    <div
                      className={`${styles.subCategoryItem} ${styles.viewMoreItem}`}
                      onClick={() => setExpandedCategoryId(mainCat.categoryId)}
                    >
                      <span className={styles.subName}>View More</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ModalContainer>
  );
};

export default MarketplaceCategories;
